import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Stack,
  Theme,
  Typography,
  styled,
  useMediaQuery,
  Box,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import useAuth from '../../providers/Auth/Auth.provider';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import TwLocationsDotsMenu from '../TwLocationsMenu/TwLocationsDotsMenu';
import TwDatePicker from '../TwDatePicker/TwDatePicker';
import DateUtils from '../../utils/dateUtils';
import { getMapsSearchUrl } from '../../utils/mapUtils';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import { FormikValues, useFormikContext } from 'formik';
import { Characters, useUnformatValue } from '../../hooks/useAllowCharacters';
import StopNoteContactInfo from './components/StopNoteContactInfo';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  MIN_STOP_COUNT,
  SliceStop,
  addStopToSegmentInOrder,
  splitSegmentAtStop,
  deleteStopV2,
  updateStopV2,
  reorderStopInSegment,
  updateTotalAutomaticMileage,
  updateDriverPayConfirmationModal,
  getMaxStopsCount
} from '../../redux/features/orders/ordersSlice';
import { LoadLocation, STOP_TYPE, TZ_USAGE } from '../../redux/api/load/load.model';
import { setAnchorId } from '../../redux/features/navigation/navigationSlice';
import useScrollToView from '../../hooks/useScrollToView';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import TwTimePicker from '../TwTimePicker/TwTimePicker';
import CheckedDateTime from './components/CheckedDateTime';
import { parse } from 'date-fns';
import { STOP_TYPES, TRIP_COVERAGE_STATUS } from '../../redux/api/trips/trips.model';
import { Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  DEFAULT_STOP,
  addStopToTripInOrder,
  deleteTripStop,
  reorderTripStop,
  updateTripLtlDriverPayConfirmationModal,
  updateTripStop
} from '../../redux/features/trip/tripSlice';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import { EntityType } from '../../redux/Global.model';
import TwLoadTooltipInfo from '../TwLoadTooltipInfo/TwLoadTooltipInfo';
import TwLoadTooltipContent from '../TwLoadTooltipInfo/TwLoadTooltipContent';
import InfoIcon from '@mui/icons-material/Info';
import './TwStop.scss';
import { locationTypeOptions } from '../../utils/location/location';
import useCheckDriverPay from '../../hooks/useCheckDriverPay';
import { useStopScroll } from '../../hooks/useStopScroll';

interface TwStopProps {
  stop: SliceStop;
  locations: TwAutocompleteOption[];
  times: TwAutocompleteOption[];
  hasLocationBeenUpdated: boolean;
  isFullscreenImportLoad?: boolean;
  isImportLoad?: boolean;
  isCreateOrder?: boolean;
  stopsTotal: number;
  segmentId: string;
  segmentOrder: number;
  stIndex: number;
  // used to determine if the stop component should use trip or order data
  // and dispatch actions to the correct slice
  entity: EntityType;
  onCreateLocationClick?: (stopUiId: string) => void;
  onEditLocationClick?: (locationId: string, stopUiId: string) => void;
  onLocationInputChange?: (value: string | null, order: number) => void;
  onLocationSelected?: (newLocation?: TwAutocompleteOption | null) => void;
}

export type TwStopHandle = {
  readonly stopOrderIndicator: HTMLSpanElement | null;
};

const TwStop = forwardRef<TwStopHandle, TwStopProps>(
  (
    {
      stop,
      isFullscreenImportLoad,
      isImportLoad,
      isCreateOrder,
      locations,
      times,
      hasLocationBeenUpdated,
      stopsTotal,
      segmentId,
      stIndex,
      segmentOrder,
      entity,
      onCreateLocationClick,
      onEditLocationClick,
      onLocationInputChange,
      onLocationSelected
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const {
      data: { userData },
      getTenantSetting
    } = useAuth();
    const enableLtl = userData?.tenant?.ltl_enabled || false;
    const { order_id, timezone_usage } = useAppSelector((state) => state.orders);
    const { tripId } = useAppSelector((state) => state.trip);
    const entity_id = entity === EntityType.TRIP ? tripId : order_id;
    const anyDriverPayExists = useCheckDriverPay({ entity });
    const {
      enableSplitLoads,
      enableAddressLocationLink,
      enableBorderConnect,
      enableMaxNumberOfStops
    } = useFeatureFlags();
    const unformatString = useUnformatValue(Characters.number);
    const stopOrderIndicatorRef = useRef<HTMLSpanElement>(null);

    const enableAppointmentWindow = getTenantSetting('enable-appointment-window', false);
    const pickupNumberEnabled = getTenantSetting('pickup-number', false);
    const use24HourClock = getTenantSetting('use-24-hour-clock', false);
    const isEnabledShowCheckInOutTimes = getTenantSetting('show-check-in-and-check-out-times');

    const allowMultiStops = useMemo<boolean>(() => {
      const isSettingEnable = getTenantSetting('allow_multi_stops_per_order', false);
      const stopsToUse = stopsTotal;

      return isSettingEnable || (!isSettingEnable && stopsToUse > MIN_STOP_COUNT);
    }, [userData?.tenantSettings, stopsTotal, stopsTotal]);

    const isAutoCalculateMileageDisabled = getTenantSetting(
      'disable-auto-calculate-mileage',
      false
    );

    const [stopType, setStopType] = useState<TwAutocompleteOption | null | undefined>(null);

    const [date, setDate] = useState<Date | null | undefined>(null);
    const [initialDate, setInitialDate] = useState<Date | null | undefined>(null);

    const [timeFrom, setTimeFrom] = useState<Date | null | undefined>(null);
    const [initialTimeFrom, setInitialTimeFrom] = useState<Date | null | undefined>(null);

    const [timeTo, setTimeTo] = useState<Date | null | undefined>(null);
    const [initialTimeTo, setInitialTimeTo] = useState<Date | null | undefined>(null);

    const [checkInDate, setCheckInDate] = useState<Date | null | undefined>(null);
    const [initialCheckInDate, setInitialCheckInDate] = useState<Date | null | undefined>(null);
    const [isCheckInEditing, setIsCheckInEditing] = useState<boolean>(false);

    const [checkInTime, setCheckInTime] = useState<Date | null | undefined>(null);
    const [initialCheckInTime, setInitialCheckInTime] = useState<Date | null | undefined>(null);

    const [checkOutDate, setCheckOutDate] = useState<Date | null | undefined>(null);
    const [initialCheckOutDate, setInitialCheckOutDate] = useState<Date | null | undefined>(null);
    const [isCheckOutEditing, setIsCheckOutEditing] = useState<boolean>(false);

    const [checkOutTime, setCheckOutTime] = useState<Date | null | undefined>(null);
    const [initialCheckOutTime, setInitialCheckOutTime] = useState<Date | null | undefined>(null);

    const maxStopsCount = useMemo(() => {
      return getMaxStopsCount(enableMaxNumberOfStops);
    }, [enableMaxNumberOfStops]);

    const { stopRef, inTimeRef, outTimeRef } = useStopScroll({
      stop,
      setIsCheckInEditing,
      setIsCheckOutEditing
    });

    useImperativeHandle(ref, () => ({
      get stopOrderIndicator(): HTMLSpanElement | null {
        return stopOrderIndicatorRef.current;
      }
    }));

    const isLastStop = useMemo<boolean>(() => {
      return stIndex === stopsTotal - 1;
    }, [stopsTotal, stIndex]);

    const isFirstStop = useMemo<boolean>(() => {
      return stIndex === 0;
    }, [stIndex]);

    useEffect(() => {
      setStopType(stop.provider_stop_type);
    }, [stop.provider_stop_type]);

    useEffect(() => {
      const pickupType = STOP_TYPES.find((t) => t.value === STOP_TYPE.PICKUP);

      if (isFirstStop && !stop.provider_stop_type) {
        setStopType(pickupType);
      }
    }, []);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_stop_type = stopType;

      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    }, [stopType]);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_date = date;
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    }, [date]);

    useEffect(() => {
      setInitialDate(stop.provider_date);
      setDate(stop.provider_date);
    }, [isImportLoad, stop.provider_date]);

    useEffect(() => {
      setInitialTimeFrom(stop.provider_time_from);
      setTimeFrom(stop.provider_time_from);
    }, [isImportLoad, stop.provider_time_from]);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_time_from = timeFrom;

      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    }, [timeFrom]);

    useEffect(() => {
      setInitialTimeTo(stop.provider_time_to);
      setTimeTo(stop.provider_time_to);
    }, [isImportLoad]);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_time_to = timeTo;

      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    }, [timeTo]);

    useEffect(() => {
      setInitialCheckInDate(stop.provider_check_in_date || null);
      setCheckInDate(stop.provider_check_in_date || null);
    }, [stop.provider_check_in_date]);

    useEffect(() => {
      setInitialCheckInTime(stop.provider_check_in_time || null);
      setCheckInTime(stop.provider_check_in_time || null);
    }, [stop.provider_check_in_time]);

    useEffect(() => {
      setInitialCheckOutDate(stop.provider_check_out_date || null);
      setCheckOutDate(stop.provider_check_out_date || null);
    }, [stop.provider_check_out_date]);

    useEffect(() => {
      setInitialCheckOutTime(stop.provider_check_out_time || null);
      setCheckOutTime(stop.provider_check_out_time || null);
    }, [stop.provider_check_out_time]);

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter') {
        // I don't use property id here because I test it in console and '' shows. so I compare against outerHTML
        if (e.currentTarget.outerHTML.includes(`stop-location-${stop.order}`)) {
          onCreateLocationClick && onCreateLocationClick(stop.ui_id);
        }
      }
    };

    const handleLocationNotesChange = (value: string, field: string): void => {
      const newStop = { ...stop, [field]: value };
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    const handleLocationContactChange = (value: string, field: string): void => {
      let newStop = { ...stop };
      if (field === 'contact_number') {
        newStop = { ...stop, contact: { ...stop.contact, contact_number: unformatString(value) } };
      } else {
        newStop = { ...stop, contact: { ...stop.contact, [field]: value } };
      }

      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    const assignIdToStops = (): string => {
      if (stop.order === stopsTotal) {
        return 'load-build-delivery-section';
      }
      if (stop.order === 1) {
        return 'load-build-pickup-section';
      }

      return '';
    };

    const disableAddStopButton = (): boolean => {
      if (stopsTotal === maxStopsCount) {
        return true;
      }

      return false;
    };

    const moveStopUp = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(
          reorderTripStop({
            originalIndex: stIndex,
            newIndex: stIndex - 1
          })
        );
      } else {
        dispatch(
          reorderStopInSegment({
            tripId: segmentId,
            originalIndex: stIndex,
            newIndex: stIndex - 1
          })
        );
      }
    };

    const moveStopDown = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        dispatch(reorderTripStop({ originalIndex: stIndex, newIndex: stIndex + 1 }));
      } else {
        dispatch(
          reorderStopInSegment({
            tripId: segmentId,
            originalIndex: stIndex,
            newIndex: stIndex + 1
          })
        );
      }
    };

    const clearInputs = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        // clear stop inputs trip slice
        if (anyDriverPayExists) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  updateTripStop({
                    stop: {
                      ...DEFAULT_STOP,
                      order: stop.order,
                      segment_id: segmentId,
                      ui_id: stop.ui_id
                    },
                    clearDriverPay: true
                  })
                )
            })
          );
        } else {
          dispatch(
            updateTripStop({
              stop: {
                ...DEFAULT_STOP,
                order: stop.order,
                segment_id: segmentId,
                ui_id: stop.ui_id
              },
              clearDriverPay: true
            })
          );
        }
      } else {
        // clear stop inputs orders slice, if driver pay exists, show confirmation modal
        // otherwise clear stop inputs
        if (anyDriverPayExists) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  updateStopV2({
                    stop: {
                      ...DEFAULT_STOP,
                      order: stop.order,
                      segment_id: segmentId,
                      ui_id: stop.ui_id
                    },
                    clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
                  })
                )
            })
          );
        } else {
          dispatch(
            updateStopV2({
              stop: {
                ...DEFAULT_STOP,
                order: stop.order,
                segment_id: segmentId,
                ui_id: stop.ui_id
              },
              clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
            })
          );
        }
      }
    };

    const splitSegment = (): void => {
      if (anyDriverPayExists) {
        dispatch(
          updateDriverPayConfirmationModal({
            open: true,
            action: () =>
              dispatch(
                splitSegmentAtStop({
                  segmentId: segmentId,
                  stopId: stop.ui_id,
                  preserveMiles: isAutoCalculateMileageDisabled
                })
              )
          })
        );
      } else {
        dispatch(
          splitSegmentAtStop({
            segmentId: segmentId,
            stopId: stop.ui_id,
            preserveMiles: isAutoCalculateMileageDisabled
          })
        );
      }
    };

    const addStopInOrder = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        if (anyDriverPayExists) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  addStopToTripInOrder({
                    order: stop.order + 1,
                    preserveMiles: isAutoCalculateMileageDisabled
                  })
                )
            })
          );
        } else {
          dispatch(
            addStopToTripInOrder({
              order: stop.order + 1,
              preserveMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      } else {
        if (anyDriverPayExists) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  addStopToSegmentInOrder({
                    segmentId: segmentId,
                    stopId: stop.ui_id,
                    direction: 'after',
                    preserveMiles: isAutoCalculateMileageDisabled
                  })
                )
            })
          );
        } else {
          dispatch(
            addStopToSegmentInOrder({
              segmentId: segmentId,
              stopId: stop.ui_id,
              direction: 'after',
              preserveMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      }
    };

    const deleteStop = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        if (anyDriverPayExists) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () => dispatch(deleteTripStop(stop.ui_id))
            })
          );
        } else {
          dispatch(deleteTripStop(stop.ui_id));
        }
      } else {
        if (anyDriverPayExists) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () => dispatch(deleteStopV2(stop.ui_id))
            })
          );
        } else {
          dispatch(deleteStopV2(stop.ui_id));
        }
      }
    };

    const handleLocationChange = (_: any, newValue: TwAutocompleteOption | string | null): void => {
      const newStop = { ...stop };

      if (newValue === null) {
        newStop.location_id = null;
        newStop.location = null;
        newStop.provider_location = null;
        newStop.contact = undefined;
        newStop.notes = null;
        newStop.provider_include_location_notes = false;
        if (enableLtl && entity === EntityType.TRIP) {
          if (anyDriverPayExists && !isAutoCalculateMileageDisabled) {
            dispatch(
              updateTripLtlDriverPayConfirmationModal({
                open: true,
                action: () =>
                  dispatch(
                    updateTripStop({
                      stop: newStop,
                      clearDriverPay: isAutoCalculateMileageDisabled ? false : true
                    })
                  )
              })
            );
          } else {
            dispatch(
              updateTripStop({
                stop: newStop,
                clearDriverPay: isAutoCalculateMileageDisabled ? false : true
              })
            );
          }
        } else {
          if (anyDriverPayExists && !isAutoCalculateMileageDisabled) {
            dispatch(
              updateDriverPayConfirmationModal({
                open: true,
                action: () => {
                  dispatch(
                    updateStopV2({
                      stop: newStop,
                      clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
                    })
                  );
                }
              })
            );
          } else {
            dispatch(
              updateStopV2({
                stop: newStop,
                clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
              })
            );
          }
        }

        // Reset total automatic mileage used on advanced rates
        dispatch(updateTotalAutomaticMileage(0));
        return;
      }

      const option = newValue as TwAutocompleteOption;
      const value = option.value as LoadLocation;

      newStop.location_id = option?.id;
      newStop.location = value || undefined;
      newStop.provider_location = option;
      newStop.contact = value
        ? {
            contact_email: value.contact_email,
            contact_name: value.contact_name,
            contact_number: value.contact_number,
            contact_number_ext: value.contact_number_ext
          }
        : undefined;

      if (
        value.notes ||
        value.contact_email ||
        value.contact_name ||
        value.contact_number ||
        value.contact_number_ext
      ) {
        newStop.notes = value.notes;
        newStop.provider_include_location_notes = true;
      } else {
        newStop.notes = undefined;
        newStop.provider_include_location_notes = false;
      }

      if (enableLtl && entity === EntityType.TRIP) {
        if (anyDriverPayExists && !isAutoCalculateMileageDisabled) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  updateTripStop({
                    stop: newStop,
                    clearDriverPay: isAutoCalculateMileageDisabled ? false : true
                  })
                )
            })
          );
        } else {
          dispatch(
            updateTripStop({
              stop: newStop,
              clearDriverPay: isAutoCalculateMileageDisabled ? false : true
            })
          );
        }
      } else {
        if (anyDriverPayExists && !isAutoCalculateMileageDisabled) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () => {
                dispatch(
                  updateStopV2({
                    stop: newStop,
                    clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
                  })
                );
              }
            })
          );
        } else {
          dispatch(
            updateStopV2({
              stop: newStop,
              clearGrossPayDrivers: isAutoCalculateMileageDisabled ? false : true
            })
          );
        }
      }

      onLocationSelected && onLocationSelected(option);
    };

    const handleAddStop = (): void => {
      if (enableLtl && entity === EntityType.TRIP) {
        if (anyDriverPayExists) {
          dispatch(
            updateTripLtlDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  addStopToTripInOrder({
                    order: stop.order,
                    preserveMiles: isAutoCalculateMileageDisabled
                  })
                )
            })
          );
        } else {
          dispatch(
            addStopToTripInOrder({
              order: stop.order,
              preserveMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      } else {
        if (anyDriverPayExists) {
          dispatch(
            updateDriverPayConfirmationModal({
              open: true,
              action: () =>
                dispatch(
                  addStopToSegmentInOrder({
                    segmentId: segmentId,
                    stopId: stop.ui_id,
                    direction: 'before',
                    preserveMiles: isAutoCalculateMileageDisabled
                  })
                )
            })
          );
        } else {
          dispatch(
            addStopToSegmentInOrder({
              segmentId: segmentId,
              stopId: stop.ui_id,
              direction: 'before',
              preserveMiles: isAutoCalculateMileageDisabled
            })
          );
        }
      }
    };

    const { errors } = useFormikContext<FormikValues>();

    const stopAddress = useMemo(() => {
      return stop.provider_location?.value as LoadLocation;
    }, [stop.provider_location]);

    const disableStop: boolean = useMemo(() => {
      if (enableLtl && entity === EntityType.ORDER) {
        return !!(stop.coverage && stop.coverage === TRIP_COVERAGE_STATUS.COVERED);
      } else {
        return false;
      }
    }, [stop.coverage]);

    /**
     * Why do we update Redux immediately in these handlers instead of using useEffect?
     *
     * - If we used useEffect to watch for changes in the local state (e.g., checkInDate),
     *   there would be a race condition: when a user edits a field and immediately saves,
     *   the Redux state might not be updated in time for the save action, causing the API
     *   request to send stale/original values instead of the user's changes.
     *
     * - By dispatching the Redux update directly in the onChange handler, we ensure that
     *   Redux always has the latest value as soon as the user makes a change. This guarantees
     *   that when the user clicks Save, the API request will include the most up-to-date data.
     *
     * - This pattern is especially important for fields that are edited and saved quickly,
     *   such as date/time pickers, where the user may not trigger a blur or other event before saving.
     */
    const handleCheckInDateChange = (date: Date | null): void => {
      setCheckInDate(date);
      const newStop = {
        ...stop,
        provider_check_in_date: date,
        provider_stop_in_time_overriden: true
      };
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckInTimeChange = (time: Date | null): void => {
      setCheckInTime(time);
      const newStop = {
        ...stop,
        provider_check_in_time: time,
        provider_stop_in_time_overriden: true
      };
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckOutDateChange = (date: Date | null): void => {
      setCheckOutDate(date);
      const newStop = {
        ...stop,
        provider_check_out_date: date,
        provider_stop_out_time_overriden: true
      };
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckOutTimeChange = (time: Date | null): void => {
      setCheckOutTime(time);
      const newStop = {
        ...stop,
        provider_check_out_time: time,
        provider_stop_out_time_overriden: true
      };
      enableLtl && entity === EntityType.TRIP
        ? dispatch(updateTripStop({ stop: newStop }))
        : dispatch(updateStopV2({ stop: newStop }));
    };

    return (
      <Draggable
        key={stop.ui_id}
        draggableId={stop.ui_id}
        index={stIndex}
        isDragDisabled={stIndex === 1 && segmentOrder > 1 && stopsTotal === 2}
      >
        {(draggableProvided): JSX.Element => (
          <div {...draggableProvided.draggableProps} ref={draggableProvided.innerRef}>
            <article className="stop__container">
              <StopDivider>
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {allowMultiStops && (
                    <>
                      <ChipButton
                        label="Add stop"
                        className="stop__add-chip"
                        disabled={disableAddStopButton()}
                        onClick={handleAddStop}
                      />
                      {!isFirstStop && enableSplitLoads && !isCreateOrder && !enableLtl && (
                        <Typography
                          component="span"
                          color="#9CA3AF"
                          fontSize="12px"
                          fontWeight="500"
                        >
                          or
                        </Typography>
                      )}
                    </>
                  )}

                  {!isFirstStop && enableSplitLoads && !isCreateOrder && !enableLtl && (
                    <ChipButton
                      label="Split trip"
                      className="stop__split-segment"
                      disabled={false}
                      onClick={splitSegment}
                    />
                  )}
                </Stack>
              </StopDivider>

              <Box className="stop__fields-container">
                <Stack flexDirection={'row'} justifyContent={'space-between'}>
                  <Box display={'flex'} alignItems={'center'}>
                    <span
                      {...draggableProvided.dragHandleProps}
                      className="stop__drag-handle"
                      title="drag-stop-handle"
                    >
                      <DragIndicatorIcon />
                    </span>

                    <Typography
                      component="span"
                      sx={{ fontWeight: '600', color: '#1F2937', fontSize: '14px' }}
                    >
                      {`Stop ${stop.order}`}
                    </Typography>

                    <Stack flexDirection={'row'} sx={{ marginLeft: '10px' }}>
                      <IconButton
                        disableRipple
                        className="stop__arrows-buttons"
                        title="move-stop-up"
                        disabled={
                          (stIndex === 0 && segmentOrder === 1) ||
                          (stIndex === 1 && segmentOrder > 1)
                        }
                        onClick={moveStopUp}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                      <IconButton
                        disableRipple
                        className="stop__arrows-buttons"
                        title="move-stop-down"
                        disabled={stopsTotal === stIndex + 1}
                        onClick={moveStopDown}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Stack alignItems={'center'} flexDirection={'row'}>
                    {!disableStop && (
                      <TwButton
                        type={TwButtonType.text}
                        action={clearInputs}
                        label="Clear inputs"
                      />
                    )}
                    {stopsTotal > MIN_STOP_COUNT && !disableStop && (
                      <CloseIcon className="stop__close-icon-v2" onClick={deleteStop} />
                    )}
                  </Stack>
                </Stack>
                <div
                  className={
                    isImportLoad || matchesMobileOrTablet
                      ? 'stop__location-container-mobile'
                      : 'stop__location-container'
                  }
                >
                  <div className="stop__location-item" id={assignIdToStops()} ref={stopRef}>
                    <TwAutocomplete
                      id={`stop-location-${stop.order}`}
                      name={`stop-location-${stop.order}`}
                      className={`w-144 stop__location ${
                        !matchesMobileOrTablet ? `stop__location--no-right-border` : ''
                      }`}
                      label="Stop location"
                      optionList={locations}
                      filterOptions={(options: any): any =>
                        options.sort((a: any, b: any) => {
                          const groupByOrder = [
                            ' ',
                            ...locationTypeOptions(enableBorderConnect).map((o) => o.label)
                          ];
                          return (
                            groupByOrder.indexOf(a.groupBy || '') -
                            groupByOrder.indexOf(b.groupBy || '')
                          );
                        })
                      }
                      value={stop.provider_location}
                      onInputChange={(_, value): void => {
                        onLocationInputChange && onLocationInputChange(value, stop.order);
                      }}
                      onChange={handleLocationChange}
                      renderOption={(props, option): React.ReactNode => {
                        return <RenderOption props={props} option={option} />;
                      }}
                      listAction={{
                        label: 'New location',
                        action: (): void => {
                          onCreateLocationClick && onCreateLocationClick(stop.ui_id);
                        }
                      }}
                      disableNoOption
                      enableClearIcon
                      onKeyDown={onKeyDownHandler}
                      disabled={disableStop}
                      grouped
                    />

                    {!matchesMobileOrTablet && !isCreateOrder && (
                      <TwLocationsDotsMenu
                        menuOptions={[
                          {
                            label: 'Create new location',
                            action: (): void => {
                              onCreateLocationClick && onCreateLocationClick(stop.ui_id);
                            },
                            disabled: false
                          },
                          {
                            label: 'Edit saved location',
                            action: (): void => {
                              onEditLocationClick &&
                                stop.provider_location &&
                                onEditLocationClick(stop.provider_location.id, stop.ui_id);
                            },
                            disabled: !stop.provider_location
                          },
                          {
                            label: 'View on Google Maps',
                            action: (): void => {
                              stop.provider_location &&
                                window.open(
                                  getMapsSearchUrl(
                                    stop.provider_location.value as LoadLocation,
                                    enableAddressLocationLink
                                  )
                                );
                            },
                            disabled: !stop.provider_location,
                            icon: (
                              <OpenInNewIcon
                                sx={{ color: '#9CA3AF', fontSize: '14px', marginLeft: '5px' }}
                              />
                            )
                          }
                        ]}
                      />
                    )}
                  </div>
                  {stopAddress && (isImportLoad || matchesMobileOrTablet) && (
                    <Box>
                      <span className="route-info__sub-title-left">{stopAddress.full_address}</span>
                      {enableLtl && entity === EntityType.TRIP && stop.order_data?.order_id && (
                        <TwLoadTooltipInfo
                          tooltipContent={
                            <TwLoadTooltipContent orderId={stop.order_data.order_id} />
                          }
                        >
                          <Typography
                            component="span"
                            className="route-info__sub-title-left"
                            sx={{
                              textDecoration: 'underline'
                            }}
                          >
                            {` • Order #${stop.order_data.order_readable_id}`}
                          </Typography>
                        </TwLoadTooltipInfo>
                      )}
                    </Box>
                  )}
                  {
                    <TwAutocomplete
                      name={`stop-type-${stop.order}`}
                      className="w-144"
                      label="Type"
                      optionList={STOP_TYPES}
                      value={stop.provider_stop_type || stopType}
                      onInputChange={(_e, value): void => {
                        if (value) {
                          const newValue =
                            value === 'Drop'
                              ? 'DROP_OFF'
                              : value === 'Pickup'
                              ? 'PICKUP'
                              : value === 'Transfer'
                              ? 'TRANSFER'
                              : value;
                          setStopType({
                            id: newValue,
                            value: newValue,
                            label: value as STOP_TYPE
                          });
                        } else {
                          setStopType(null);
                        }
                      }}
                      onChange={(_, newValue: TwAutocompleteOption | null): void => {
                        const newStop = { ...stop };
                        newStop.stop_type = newValue?.id as STOP_TYPE;
                        newStop.provider_stop_type = newValue;
                        enableLtl && entity === EntityType.TRIP
                          ? dispatch(updateTripStop({ stop: newStop }))
                          : dispatch(updateStopV2({ stop: newStop }));
                      }}
                      enableClearIcon
                      disabled={disableStop}
                    />
                  }
                </div>
                {stopAddress && !isImportLoad && !matchesMobileOrTablet && (
                  <Box>
                    <span className="route-info__sub-title-left">{stopAddress.full_address}</span>
                    {enableLtl && entity === EntityType.TRIP && stop.order_data?.order_id && (
                      <TwLoadTooltipInfo
                        tooltipContent={
                          <TwLoadTooltipContent orderId={stop.order_data?.order_id} />
                        }
                      >
                        <Typography
                          component="span"
                          className="route-info__sub-title-left"
                          sx={{
                            textDecoration: 'underline'
                          }}
                        >
                          {` • Order #${stop.order_data.order_readable_id}`}
                        </Typography>
                      </TwLoadTooltipInfo>
                    )}
                  </Box>
                )}

                {
                  <StopNoteContactInfo
                    isNoteVisible={!!stop.provider_include_location_notes}
                    hasLocationBeenUpdated={hasLocationBeenUpdated}
                    toggleNoteVisibility={(): void => {
                      const newStop = {
                        ...stop,
                        provider_include_location_notes: !stop.provider_include_location_notes
                      };
                      enableLtl && entity === EntityType.TRIP
                        ? dispatch(updateTripStop({ stop: newStop }))
                        : dispatch(updateStopV2({ stop: newStop }));
                    }}
                    initialNote={stop.notes || ''}
                    initialEmail={stop.contact?.contact_email || ''}
                    initialName={stop.contact?.contact_name || ''}
                    initialPhone={stop.contact?.contact_number || ''}
                    initialExt={stop.contact?.contact_number_ext || ''}
                    handleFieldChange={handleLocationNotesChange}
                    handleLocationContactChange={handleLocationContactChange}
                    isImportLoad={isImportLoad}
                    disableCheckbox={disableStop}
                  />
                }
                {
                  <Box
                    flexDirection={isImportLoad || matchesMobileOrTablet ? 'column' : 'row'}
                    className={
                      isImportLoad || matchesMobileOrTablet
                        ? 'stop__import-date-time'
                        : 'stop__date-time'
                    }
                  >
                    <TwDatePicker
                      name={`stop-date-${stop.order}`}
                      label={`Scheduled date *`}
                      containerClassName="stop__date"
                      value={date || initialDate}
                      placeholder="MM/DD/YY"
                      onChange={(newDate): void => {
                        const date = newDate as Date | null;
                        setDate(date);
                      }}
                      onInputBlur={(e): void => {
                        try {
                          const date = new Date(e.target.value);
                          setDate(date.toISOString() ? date : null);
                        } catch (e) {
                          setDate(null);
                        }
                      }}
                      error={!!errors[`stop-date-${stop.order}`]}
                      disabled={disableStop}
                    />
                    <Box
                      className={!isCreateOrder ? 'stop__time-container' : ''}
                      display={isCreateOrder ? 'flex' : 'initial'}
                    >
                      <>
                        <TwTimePicker
                          name={`stop-time-from-${stop.order}`}
                          label={`${enableAppointmentWindow ? 'From' : 'Scheduled time'} (${
                            timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                              ? (stop.provider_location?.value as LoadLocation)?.short_offset
                              : userData?.short_offset
                          })`}
                          value={timeFrom || initialTimeFrom}
                          containerClassName={
                            isImportLoad ? 'stop__import-time-hide' : 'stop__time-hide'
                          }
                          onChange={(value): void => {
                            const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                            const date = value as Date | null;
                            validInput ? setTimeFrom(date) : setTimeFrom(null);
                          }}
                          onInputBlur={(e): void => {
                            const validInput =
                              e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                            try {
                              const date = parse(
                                e.target.value as string,
                                !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                                new Date()
                              );

                              validInput ? setTimeFrom(date) : setTimeFrom(null);
                            } catch (e) {
                              setTimeFrom(null);
                            }
                          }}
                          ampm={!use24HourClock}
                          placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                          disabled={disableStop}
                        />

                        {enableAppointmentWindow && (
                          // Tabindex here helps focus on the second time field which was often missed in keyboard navigation
                          <div tabIndex={0}>
                            <TwTimePicker
                              name={`stop-time-to-${stop.order}`}
                              label={`To (${
                                timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                                  ? (stop.provider_location?.value as LoadLocation)?.short_offset
                                  : userData?.short_offset
                              })`}
                              value={timeTo || initialTimeTo}
                              containerClassName={
                                isImportLoad ? 'stop__import-time-hide' : 'stop__time-hide'
                              }
                              onChange={(value): void => {
                                const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                                const date = value as Date | null;
                                validInput ? setTimeTo(date) : setTimeTo(null);
                              }}
                              onInputBlur={(e): void => {
                                const validInput =
                                  e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                                try {
                                  const date = parse(
                                    e.target.value as string,
                                    !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                                    new Date()
                                  );
                                  validInput ? setTimeTo(date) : setTimeTo(null);
                                } catch (e) {
                                  setTimeTo(null);
                                }
                              }}
                              ampm={!use24HourClock}
                              placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                              disabled={disableStop}
                            />
                          </div>
                        )}
                      </>
                    </Box>
                  </Box>
                }
                {enableLtl && entity === EntityType.ORDER ? (
                  <></>
                ) : (
                  <>
                    {' '}
                    {!isCheckInEditing && checkInDate && checkInTime && !isCreateOrder && (
                      <Box display="flex" flexDirection="column">
                        <CheckedDateTime
                          date={checkInDate}
                          time={checkInTime}
                          label="Actual in time: "
                          onEditClick={(): void => {
                            setIsCheckInEditing(true);
                          }}
                          shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                          use24HourFormat={use24HourClock}
                        />
                      </Box>
                    )}
                    {isCheckInEditing && isEnabledShowCheckInOutTimes && !isCreateOrder && (
                      <Box
                        ref={inTimeRef}
                        id={`stop-check-in-time-${stop.segment_id}-${stop.id}`}
                        flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
                        className={
                          matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'
                        }
                      >
                        <TwDatePicker
                          name={`stop-check-in-date-${stop.order}`}
                          label="Actual in date*"
                          containerClassName="stop__date"
                          value={checkInDate || initialCheckInDate}
                          placeholder="MM/DD/YY"
                          onChange={(newDate): void => {
                            handleCheckInDateChange(newDate as Date | null);
                          }}
                          onInputBlur={(e): void => {
                            try {
                              const date = new Date(e.target.value);
                              handleCheckInDateChange(date.toISOString() ? date : null);
                            } catch (e) {
                              handleCheckInDateChange(null);
                            }
                          }}
                          error={!!errors[`stop-check-in-date-${stop.order}`]}
                        />
                        <TwTimePicker
                          name={`stop-check-in-time-${stop.order}`}
                          label={`Actual in time (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                          value={checkInTime || initialCheckInTime}
                          containerClassName="stop__time-hide"
                          onChange={(value): void => {
                            const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                            handleCheckInTimeChange(validInput ? (value as Date | null) : null);
                          }}
                          onInputBlur={(e): void => {
                            try {
                              const date = parse(
                                e.target.value as string,
                                !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                                new Date()
                              );
                              handleCheckInTimeChange(date);
                            } catch (e) {
                              handleCheckInTimeChange(null);
                            }
                          }}
                          ampm={!use24HourClock}
                          placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                          error={!!errors[`stop-check-in-time-${stop.order}`]}
                        />
                      </Box>
                    )}
                    {!isCheckOutEditing &&
                      checkOutDate &&
                      checkOutTime &&
                      isEnabledShowCheckInOutTimes &&
                      !isCreateOrder && (
                        <Box display="flex" flexDirection="column">
                          <CheckedDateTime
                            date={checkOutDate}
                            time={checkOutTime}
                            label="Actual out time: "
                            onEditClick={(): void => {
                              setIsCheckOutEditing(true);
                            }}
                            shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                            use24HourFormat={use24HourClock}
                          />
                        </Box>
                      )}
                    {isCheckOutEditing && isEnabledShowCheckInOutTimes && !isCreateOrder && (
                      <Box
                        ref={outTimeRef}
                        id={`stop-check-out-time-${stop.segment_id}-${stop.id}`}
                        flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
                        className={
                          matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'
                        }
                      >
                        <TwDatePicker
                          name={`stop-check-out-date-${stop.order}`}
                          label="Actual out date*"
                          containerClassName="stop__date"
                          value={checkOutDate || initialCheckOutDate}
                          placeholder="MM/DD/YY"
                          onChange={(newDate): void => {
                            handleCheckOutDateChange(newDate as Date | null);
                          }}
                          onInputBlur={(e): void => {
                            try {
                              const date = new Date(e.target.value);
                              handleCheckOutDateChange(date.toISOString() ? date : null);
                            } catch (e) {
                              handleCheckOutDateChange(null);
                            }
                          }}
                          error={!!errors[`stop-check-out-date-${stop.order}`]}
                        />
                        <Box display={'flex'} flexDirection={'row'}>
                          <TwTimePicker
                            name={`stop-check-out-time-${stop.order}`}
                            label={`Actual out time (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                            value={checkOutTime || initialCheckOutTime}
                            containerClassName="stop__time-hide"
                            onChange={(value): void => {
                              const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                              handleCheckOutTimeChange(validInput ? (value as Date | null) : null);
                            }}
                            onInputBlur={(e): void => {
                              try {
                                const date = parse(
                                  e.target.value as string,
                                  !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                                  new Date()
                                );
                                handleCheckOutTimeChange(date);
                              } catch (e) {
                                handleCheckOutTimeChange(null);
                              }
                            }}
                            ampm={!use24HourClock}
                            placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                            error={!!errors[`stop-check-out-time-${stop.order}`]}
                          />
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {pickupNumberEnabled && (stop.stop_type || stop.provider_stop_type) && (
                  <div className="stop__pickdrop-number">
                    <TwField
                      id="pickupNumber"
                      name="pickupNumber"
                      label={
                        stop.stop_type === STOP_TYPE.PICKUP ||
                        stop.provider_stop_type?.value === STOP_TYPE.PICKUP
                          ? 'Pick-up #'
                          : stop.stop_type === STOP_TYPE.DROP_OFF ||
                            stop.provider_stop_type?.value === STOP_TYPE.DROP_OFF
                          ? 'Delivery #'
                          : 'Transfer #'
                      }
                      inputType={TwInputTypes.text}
                      value={stop.provider_stop_identifier}
                      onInputChange={(e): void => {
                        const newStop = { ...stop };
                        newStop.provider_stop_identifier = e.target.value;
                        newStop.stop_identifier = e.target.value;

                        enableLtl && entity === EntityType.TRIP
                          ? dispatch(updateTripStop({ stop: newStop }))
                          : dispatch(updateStopV2({ stop: newStop }));
                      }}
                      disabled={disableStop}
                    />
                  </div>
                )}

                {disableStop && (
                  <Typography
                    component={'span'}
                    className="route-info__sub-title-left"
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {' '}
                    <InfoIcon
                      sx={{ width: '1rem', height: '1rem', color: '#D1D5DB', marginRight: '1rem' }}
                    />
                    This stop is assigned to a trip and can be edited only at a trip level.
                  </Typography>
                )}
              </Box>
              {allowMultiStops && isLastStop && (
                <StopDivider>
                  <ChipButton
                    label="Add stop"
                    className="stop__add-chip"
                    disabled={disableAddStopButton()}
                    onClick={addStopInOrder}
                  />
                </StopDivider>
              )}
            </article>
          </div>
        )}
      </Draggable>
    );
  }
);

TwStop.displayName = 'TwStop';

export default TwStop;

const StopDivider = styled(Divider)(() => ({
  '.MuiDivider-wrapper': {
    padding: 0
  },
  ':before': {
    top: 0
  },
  ':after': {
    top: 0
  }
}));

const ChipButton = styled(Chip)(() => ({
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1F2937',
  backgroundColor: 'transparent',
  borderRadius: '20px',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#FFF',
    border: '1px solid #D1D5DB'
  }
}));

export const StopOrderV2 = forwardRef<
  HTMLSpanElement,
  { islast: string; children: React.ReactNode }
>((props, ref) => (
  <span
    ref={ref}
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '22px',
      height: '22px',
      background: '#9CA3AF',
      borderRadius: props.islast === 'true' ? '0' : '20px',
      color: '#fff',
      fontWeight: 500,
      fontSize: '12px',
      marginRight: '10px'
    }}
  >
    {props.children}
  </span>
));

StopOrderV2.displayName = 'StopOrderV2';
